<script setup>
import { useLayout } from '@/layout/common/composables/layout';
import { cva } from 'class-variance-authority';
import { computed, inject, onMounted } from 'vue';

const props = defineProps({
    class: {
        type: String,
        default: ''
    },
    asChild: {
        type: Boolean,
        default: false
    },
    isActive: {
        type: Boolean,
        default: false
    },
    to: {
        type: String,
        default: ''
    },
    href: {
        type: String,
        default: ''
    },
    target: {
        type: String,
        default: '_self'
    },
    mode: {
        type: String,
        default: null
    },
    isFirst: {
        type: Boolean,
        default: false
    },
    tooltipValue: {
        type: String,
        default: ''
    }
});

const menuContext = inject('menuContext', null);

const handleClick = (event) => {
    if (menuContext?.collapsible && !props.to && !props.href) {
        event.preventDefault();
        menuContext.toggle();
    }
};

onMounted(() => {
    if (props.isActive && menuContext?.collapsible) {
        menuContext.open();
    }
});

const { layoutConfig, calculatedMenuMode } = useLayout();

const calculatedVariant = computed(() => {
    if (props.mode) {
        return props.mode;
    }
    return calculatedMenuMode.value;
});

const sidebarMenuButtonVariants = cva(
    'group inline-flex w-full items-center gap-3 rounded-xl px-4 py-2 font-medium text-surface-500 data-[active=false]:hover:bg-surface-200 dark:data-[active=false]:hover:bg-surface-800 data-[active=true]:bg-primary-active data-[active=true]:text-primary-active-contrast transition-all [&_span]:whitespace-nowrap [&_span]:truncate',
    {
        variants: {
            variant: {
                slim: 'w-16 h-auto !py-2 flex-col [&_span]:text-[11px] !px-1 !rounded-lg  [&_span]:w-fit [&_span]:truncate [&_span]:max-w-full',
                compact: 'w-9 h-9 !p-[0.675rem]',
                drawer: 'data-[state=collapsed]:!p-[0.675rem] data-[state=collapsed]:w-9 data-[state=expanded]:w-full h-9',
                reveal: 'float-right flex-row-reverse justify-between h-9 data-[state=collapsed]:!pr-[0.675rem] !w-full'
            }
        }
    }
);

const sidebarMenuButtonClass = computed(() => {
    return sidebarMenuButtonVariants({ variant: calculatedVariant.value, class: props.class });
});
</script>

<template>
    <component
        :is="props?.asChild ? 'div' : props?.href ? 'a' : props?.to ? 'router-link' : 'button'"
        :data-active="props?.isActive"
        :href="props?.href || props?.to"
        :target="props?.target"
        :to="props?.to"
        @click="handleClick"
        :data-variant="calculatedVariant"
        :data-state="layoutConfig.menuState"
        :class="[props.asChild ? props.class : [sidebarMenuButtonClass, calculatedVariant === 'reveal' && props.isFirst ? '!w-[15rem]' : '', menuContext?.isOpen.value ? 'bg-surface-200/75 dark:bg-surface-800/75' : '', props.class]]"
        v-tooltip="{ value: tooltipValue }"
    >
        <slot />
        <span
            v-if="menuContext?.collapsible || calculatedVariant === 'horizontal'"
            class="group-data-[state=collapsed]:opacity-0 transition-opacity duration-300"
            :class="[calculatedVariant === 'reveal' ? 'right-12' : 'right-4', calculatedVariant === 'horizontal' ? 'static' : 'absolute']"
        >
            <i class="pi pi-chevron-down transition-transform !text-xs group-data-[popover-open=true]/sidebar-popover:rotate-180" :class="{ 'rotate-180': menuContext.isOpen.value }" />
        </span>
    </component>
</template>
